import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import CostCodeTypeahead from './CostCodeTypeahead';
import { explainCalculationError, parameterPrimary, runStatusLabel } from './calculations/productCopy';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const OPERATORS = [
  { value: 'add', label: '+' },
  { value: 'sub', label: '−' },
  { value: 'mul', label: '×' },
  { value: 'div', label: '÷' },
];

function slug(label, fallback) {
  const text = String(label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return text || fallback;
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function emptyTerm() {
  return { kind: '', key: '', value: '' };
}

function emptyNamed() {
  return { id: uid(), label: '', key: '', terms: [emptyTerm()], operators: [] };
}

function emptyParameter() {
  return { id: uid(), label: '', key: '', value: '' };
}

function uniqueKey(label, taken) {
  const base = slug(label, '');
  if (!base) return '';
  if (!taken.includes(base)) return base;
  let suffix = 2;
  while (taken.includes(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
}

function applyLabel(rows, id, label) {
  const current = rows.find((row) => row.id === id);
  if (!current) return { rows, oldKey: '', newKey: '' };
  const taken = rows.filter((row) => row.id !== id && row.key).map((row) => row.key);
  const nextKey = uniqueKey(label, taken) || current.key;
  return {
    rows: rows.map((row) => (row.id === id ? { ...row, label, key: nextKey } : row)),
    oldKey: current.key,
    newKey: nextKey,
  };
}

function retarget(namedValues, kind, oldKey, newKey) {
  if (!oldKey || oldKey === newKey) return namedValues;
  return namedValues.map((named) => ({
    ...named,
    terms: named.terms.map((term) => (
      term.kind === kind && term.key === oldKey ? { ...term, key: newKey } : term
    )),
  }));
}

function emptyPair() {
  return { cost_code_vuid: '', cost_code_label: '', cost_type_vuid: '' };
}

function emptyGroup() {
  return { id: uid(), label: '', pairs: [emptyPair()] };
}

function termToNode(term) {
  if (term.kind === 'input' && term.key) return { ref: 'input', key: term.key };
  if (term.kind === 'parameter' && term.key) return { ref: 'parameter', key: term.key };
  if (term.kind === 'named' && term.key) return { ref: 'named', key: term.key };
  if (term.kind === 'constant' && String(term.value).trim() !== '') return { const: String(term.value).trim() };
  return null;
}

function formulaTree(named) {
  const nodes = named.terms.map(termToNode);
  if (nodes.some((node) => !node)) return null;
  let tree = nodes[0];
  for (let index = 0; index < named.operators.length; index += 1) {
    tree = { op: named.operators[index], args: [tree, nodes[index + 1]] };
  }
  return tree;
}

export default function CalculationBuilder() {
  const { projectVuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const inPreview = location.pathname.startsWith('/procore/');
  const settingsPath = inPreview
    ? `/procore/projects/${projectVuid}/settings`
    : `/projects/${projectVuid}/settings`;

  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [projectCodesOnly, setProjectCodesOnly] = useState(false);
  const [periodVuid, setPeriodVuid] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parameters, setParameters] = useState([emptyParameter()]);
  const [groups, setGroups] = useState([emptyGroup()]);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const [namedValues, setNamedValues] = useState([emptyNamed()]);
  const [renamingId, setRenamingId] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [runInputValues, setRunInputValues] = useState({});

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${baseURL}/api/calculations/providers`),
      axios.get(`${baseURL}/api/accounting-periods`),
      axios.get(`${baseURL}/api/cost-types`),
      axios.get(`${baseURL}/api/projects/${projectVuid}/settings`),
    ]).then(([providerRes, periodRes, typeRes, settingsRes]) => {
      if (cancelled) return;
      setProviders(providerRes.data?.providers || []);
      const periodRows = Array.isArray(periodRes.data) ? periodRes.data : [];
      setPeriods(periodRows);
      const open = periodRows.find((period) => String(period.status || '').toLowerCase() === 'open');
      if (open) setPeriodVuid(open.vuid);
      setCostTypes(Array.isArray(typeRes.data) ? typeRes.data : []);
      setProjectCodesOnly(!!settingsRes.data?.allow_project_cost_codes);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Could not load the calculation builder.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  const typeLabel = (vuid) => {
    const row = costTypes.find((item) => item.vuid === vuid);
    if (!row) return '';
    return row.abbreviation || row.cost_type || '';
  };

  const updateParameter = (id, patch) => {
    setParameters((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const renameParameter = (id, label) => {
    const result = applyLabel(parameters, id, label);
    setParameters(result.rows);
    if (result.oldKey !== result.newKey) {
      setNamedValues((rows) => retarget(rows, 'parameter', result.oldKey, result.newKey));
    }
  };

  const updateNamed = (id, patch) => {
    setNamedValues((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const renameCalculated = (id, label) => {
    const result = applyLabel(namedValues, id, label);
    setNamedValues(result.oldKey === result.newKey
      ? result.rows
      : retarget(result.rows, 'named', result.oldKey, result.newKey));
  };

  const updateGroup = (id, patch) => {
    setGroups((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const toggleGroup = (id) => {
    setExpandedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runPreview = async () => {
    setError('');
    setPreview(null);
    const namedPayload = [];
    for (const named of namedValues) {
      const formula = formulaTree(named);
      if (!formula || !named.key) {
        setError('Each calculated value needs a name and a complete formula. Nothing was changed.');
        return;
      }
      namedPayload.push({ key: named.key, label: named.label || named.key, formula });
    }
    setRunning(true);
    try {
      const response = await axios.post(`${baseURL}/api/calculations/preview`, {
        project_vuid: projectVuid,
        accounting_period_vuid: periodVuid,
        name,
        description,
        parameters: parameters.filter((row) => row.key).map((row) => ({
          key: row.key,
          label: row.label || row.key,
          value: row.value,
        })),
        groups: groups.map((group, index) => ({
          key: group.id,
          label: group.label || `Source / Scope ${index + 1}`,
          pairs: group.pairs.map((pair) => ({
            cost_code_vuid: pair.cost_code_vuid,
            cost_type_vuid: pair.cost_type_vuid,
            cost_code_label: pair.cost_code_label,
            cost_type_label: typeLabel(pair.cost_type_vuid),
          })),
        })),
        named_values: namedPayload,
        run_inputs: providers
          .filter((provider) => provider.evaluation_behavior === 'run_entered')
          .filter((provider) => namedValues.some((named) => named.terms.some((term) => term.kind === 'input' && term.key === provider.key)))
          .filter((provider) => String(runInputValues[provider.key] ?? '').trim() !== '')
          .map((provider) => ({ key: provider.key, value: String(runInputValues[provider.key]).trim() })),
      });
      setPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Preview failed.');
    } finally {
      setRunning(false);
    }
  };

  const referencedRunProviders = providers.filter((provider) => (
    provider.evaluation_behavior === 'run_entered'
    && namedValues.some((named) => named.terms.some((term) => term.kind === 'input' && term.key === provider.key))
  ));

  if (loading) {
    return <p className="text-gray-600">Loading calculation builder…</p>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calculation Builder</h1>
          <p className="text-gray-600 mt-1">Preview a calculation against this project. Nothing is saved or posted.</p>
        </div>
        <button type="button" className="btn-claret" onClick={() => navigate(settingsPath)}>Back to Project Settings</button>
      </div>

      {error ? <div className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div> : null}

      <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Calculation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-sm font-medium text-gray-700">
            Name
            <input className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Accounting period
            <select className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" value={periodVuid} onChange={(event) => setPeriodVuid(event.target.value)}>
              <option value="">Select a period</option>
              {periods.map((period) => (
                <option key={period.vuid} value={period.vuid}>
                  {period.month}/{period.year} ({period.status})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700 md:col-span-2">
            Description
            <textarea className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Parameters</h2>
          <button type="button" className="btn-claret" onClick={() => setParameters((rows) => [...rows, emptyParameter()])}>Add parameter</button>
        </div>
        <p className="text-sm text-gray-600 mb-4">Rates and other numbers the formulas can use.</p>
        {parameters.map((row) => (
          <div key={row.id} className="flex items-center gap-3 mb-3">
            <input
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Name"
              value={row.label}
              onChange={(event) => renameParameter(row.id, event.target.value)}
            />
            <input
              className="w-36 px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Value"
              value={row.value}
              onChange={(event) => updateParameter(row.id, { value: event.target.value })}
            />
            <button type="button" className="text-sm underline" onClick={() => setParameters((rows) => rows.filter((item) => item.id !== row.id))}>Remove</button>
          </div>
        ))}
      </section>

      <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Source / Scope</h2>
          <button
            type="button"
            className="btn-claret"
            onClick={() => {
              const group = emptyGroup();
              setGroups((rows) => [...rows, group]);
              setExpandedGroupIds((current) => new Set(current).add(group.id));
            }}
          >
            Add source group
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">Each group is evaluated on its own cost code and cost type pairs. Scoped project data, such as billing or cost, is recalculated for that group. A manual amount is not.</p>
        {groups.map((group, groupIndex) => {
          const expanded = expandedGroupIds.has(group.id);
          const pairCount = group.pairs.filter((pair) => pair.cost_code_vuid && pair.cost_type_vuid).length;
          const title = group.label || `Source / Scope ${groupIndex + 1}`;
          return (
          <div key={group.id} className="border border-gray-200 rounded-md mb-4">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <button
                type="button"
                className="flex-1 text-left"
                aria-expanded={expanded}
                onClick={() => toggleGroup(group.id)}
              >
                <span className="font-medium text-gray-900">{title}</span>
                <span className="ml-3 text-sm text-gray-500">
                  {pairCount} source pair{pairCount === 1 ? '' : 's'}
                </span>
              </button>
              <button type="button" className="text-sm underline" onClick={() => toggleGroup(group.id)}>{expanded ? 'Hide' : 'Show'}</button>
              <button
                type="button"
                className="text-sm underline"
                onClick={() => {
                  setGroups((rows) => rows.filter((item) => item.id !== group.id));
                  setExpandedGroupIds((current) => {
                    const next = new Set(current);
                    next.delete(group.id);
                    return next;
                  });
                }}
              >
                Remove
              </button>
            </div>
            {expanded ? (
            <div className="px-4 pb-4">
            <div className="mb-3">
              <input
                className="px-3 py-2 border border-gray-300 rounded-md w-full"
                placeholder={`Source / Scope ${groupIndex + 1}`}
                value={group.label}
                onChange={(event) => updateGroup(group.id, { label: event.target.value })}
              />
            </div>
            {group.pairs.map((pair, pairIndex) => (
              <div key={`${group.id}-${pairIndex}`} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <CostCodeTypeahead
                  projectVuid={projectVuid}
                  projectCodesOnly={projectCodesOnly}
                  value={pair.cost_code_vuid}
                  selectedLabel={pair.cost_code_label}
                  onChange={(vuid, row) => {
                    const pairs = group.pairs.map((item, index) => (
                      index === pairIndex
                        ? { ...item, cost_code_vuid: vuid || '', cost_code_label: row ? `${row.code || ''} — ${row.description || ''}`.replace(/ — $/, '') : '' }
                        : item
                    ));
                    updateGroup(group.id, { pairs });
                  }}
                />
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md"
                  value={pair.cost_type_vuid}
                  onChange={(event) => {
                    const pairs = group.pairs.map((item, index) => (
                      index === pairIndex ? { ...item, cost_type_vuid: event.target.value } : item
                    ));
                    updateGroup(group.id, { pairs });
                  }}
                >
                  <option value="">Cost type</option>
                  {costTypes.map((type) => (
                    <option key={type.vuid} value={type.vuid}>{type.abbreviation || type.cost_type}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="text-sm underline text-left"
                  onClick={() => updateGroup(group.id, { pairs: group.pairs.filter((_, index) => index !== pairIndex) })}
                >
                  Remove source
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-sm underline"
              onClick={() => updateGroup(group.id, { pairs: [...group.pairs, emptyPair()] })}
            >
              Add source pair
            </button>
            </div>
            ) : null}
          </div>
          );
        })}
      </section>

      <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Calculated Values</h2>
          <button
            type="button"
            className="btn-claret"
            onClick={() => {
              const created = emptyNamed();
              setNamedValues((rows) => [...rows, created]);
              setRenamingId(created.id);
              setMenuId(null);
            }}
          >
            Add calculated value
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">These are the values this calculation produces.</p>
        {namedValues.map((named) => (
          <CalculatedValueCard
            key={named.id}
            named={named}
            providers={providers}
            parameters={parameters}
            namedValues={namedValues}
            renaming={renamingId === named.id || !named.label}
            menuOpen={menuId === named.id}
            onRename={(label) => renameCalculated(named.id, label)}
            onFinishRename={() => setRenamingId(null)}
            onToggleMenu={() => setMenuId((current) => (current === named.id ? null : named.id))}
            onCloseMenu={() => setMenuId(null)}
            onBeginRename={() => {
              setRenamingId(named.id);
              setMenuId(null);
            }}
            onDuplicate={() => {
              const copy = {
                ...named,
                id: uid(),
                label: named.label ? `${named.label} copy` : '',
                key: uniqueKey(named.label ? `${named.label} copy` : 'copy', namedValues.map((item) => item.key).filter(Boolean)),
                terms: named.terms.map((term) => ({ ...term })),
                operators: [...named.operators],
              };
              setNamedValues((rows) => {
                const index = rows.findIndex((item) => item.id === named.id);
                const next = rows.slice();
                next.splice(index + 1, 0, copy);
                return next;
              });
              setRenamingId(copy.id);
              setMenuId(null);
            }}
            onDelete={() => {
              setNamedValues((rows) => rows.filter((item) => item.id !== named.id));
              setMenuId(null);
            }}
            onChangeTerm={(termIndex, next) => {
              const terms = named.terms.map((item, index) => (index === termIndex ? next : item));
              updateNamed(named.id, { terms });
            }}
            onChangeOperator={(operatorIndex, value) => {
              const operators = named.operators.slice();
              operators[operatorIndex] = value;
              updateNamed(named.id, { operators });
            }}
            onRemoveTerm={(termIndex) => {
              if (named.terms.length === 1) {
                updateNamed(named.id, { terms: [emptyTerm()], operators: [] });
                return;
              }
              updateNamed(named.id, {
                terms: named.terms.filter((_, index) => index !== termIndex),
                operators: named.operators.filter((_, index) => index !== (termIndex === 0 ? 0 : termIndex - 1)),
              });
            }}
            onAddStep={() => updateNamed(named.id, {
              terms: [...named.terms, emptyTerm()],
              operators: [...named.operators, 'mul'],
            })}
          />
        ))}
      </section>

      {referencedRunProviders.length ? (
        <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Run Inputs</h2>
          <p className="text-sm text-gray-600 mb-4">Entered for this preview only. These are not parameters, and they are not filtered by Source / Scope.</p>
          {referencedRunProviders.map((provider) => (
            <label key={provider.key} className="block text-sm font-medium text-gray-700 mb-3">
              {provider.name}
              <input
                className="mt-1 w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Amount"
                value={runInputValues[provider.key] || ''}
                onChange={(event) => setRunInputValues((current) => ({ ...current, [provider.key]: event.target.value }))}
              />
              <span className="mt-1 block text-xs font-normal text-gray-500">{provider.description}</span>
            </label>
          ))}
        </section>
      ) : null}

      <div className="flex gap-3 mb-6">
        <button type="button" className="btn-claret" onClick={runPreview} disabled={running || !periodVuid}>
          {running ? 'Previewing…' : 'Preview Calculation'}
        </button>
      </div>

      {preview ? <PreviewResult preview={preview} /> : null}
    </div>
  );
}

function termLabel(term, providers, parameters, namedValues) {
  if (term.kind === 'input') {
    return providers.find((item) => item.key === term.key)?.name || (term.key ? 'Missing value' : 'Choose');
  }
  if (term.kind === 'parameter') {
    return parameters.find((item) => item.key === term.key)?.label || (term.key ? 'Missing value' : 'Choose');
  }
  if (term.kind === 'named') {
    return namedValues.find((item) => item.key === term.key)?.label || (term.key ? 'Missing value' : 'Choose');
  }
  if (term.kind === 'constant') return term.value;
  return '';
}

function chipClass(kind) {
  const base = 'inline-flex items-center max-w-xs px-2.5 py-1 rounded-md border text-sm text-left';
  if (!kind) return `${base} border-dashed border-gray-300 bg-white text-gray-500`;
  if (kind === 'parameter') return `${base} border-gray-300 bg-gray-50 text-gray-900`;
  if (kind === 'named') return `${base} border-gray-400 bg-white font-medium text-gray-900`;
  return `${base} border-gray-300 bg-white text-gray-900`;
}

function CalculatedValueCard({
  named,
  providers,
  parameters,
  namedValues,
  renaming,
  menuOpen,
  onRename,
  onFinishRename,
  onToggleMenu,
  onCloseMenu,
  onBeginRename,
  onDuplicate,
  onDelete,
  onChangeTerm,
  onChangeOperator,
  onRemoveTerm,
  onAddStep,
  locked = false,
}) {
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (!menuRef.current?.contains(event.target)) onCloseMenu();
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen, onCloseMenu]);

  return (
    <div className="border border-gray-200 rounded-md p-4 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          {renaming ? (
            <input
              autoFocus
              className="w-full text-lg font-semibold text-gray-900 px-2 py-1 border border-gray-300 rounded-md"
              placeholder="Name"
              value={named.label}
              onChange={(event) => onRename(event.target.value)}
              onBlur={() => { if (named.label) onFinishRename(); }}
              onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
            />
          ) : (
            <h3 className="text-lg font-semibold text-gray-900">{named.label}</h3>
          )}
        </div>
        {locked ? null : <div className="relative" ref={menuRef}>
          <button
            type="button"
            className="px-2 py-1 text-gray-700 border border-gray-300 rounded bg-white hover:bg-gray-50"
            aria-label={`Actions for ${named.label || 'calculated value'}`}
            aria-expanded={menuOpen}
            onClick={onToggleMenu}
          >
            •••
          </button>
          {menuOpen ? (
            <div className="absolute right-0 top-full z-20 mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-lg py-1">
              <button type="button" className="block w-full text-left px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-50" onClick={onBeginRename}>Rename</button>
              <button type="button" className="block w-full text-left px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-50" onClick={onDuplicate}>Duplicate</button>
              <button type="button" className="block w-full text-left px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-50" onClick={onDelete}>Delete</button>
            </div>
          ) : null}
        </div>}
      </div>
      <div className="text-xs font-semibold text-gray-500 mb-2">Formula</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {named.terms.map((term, termIndex) => (
          <React.Fragment key={`${named.id}-term-${termIndex}`}>
            {termIndex > 0 ? (
              locked ? (
                <span className="text-base text-gray-900">{OPERATORS.find((op) => op.value === (named.operators[termIndex - 1] || 'mul'))?.label || '×'}</span>
              ) : (
              <select
                aria-label="Operator"
                className="w-11 h-8 text-center text-base border border-gray-300 rounded-md bg-white"
                value={named.operators[termIndex - 1] || 'mul'}
                onChange={(event) => onChangeOperator(termIndex - 1, event.target.value)}
              >
                {OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              )
            ) : null}
            <OperandToken
              term={term}
              providers={providers}
              parameters={parameters}
              namedValues={namedValues.filter((item) => item.id !== named.id && item.key && item.label)}
              canRemove={!locked && (named.terms.length > 1 || !!term.kind)}
              locked={locked}
              onChange={(next) => onChangeTerm(termIndex, next)}
              onRemove={() => onRemoveTerm(termIndex)}
            />
          </React.Fragment>
        ))}
      </div>
      {locked ? null : (
      <button
        type="button"
        className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-900 border border-gray-300 rounded bg-white hover:bg-gray-50"
        onClick={onAddStep}
      >
        + Add to formula
      </button>
      )}
    </div>
  );
}

function OperandToken({ term, providers, parameters, namedValues, canRemove, onChange, onRemove, locked = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const label = termLabel(term, providers, parameters, namedValues);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = (next) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <span className="relative inline-flex items-center" ref={wrapRef}>
      <span className={chipClass(term.kind)}>
        {locked ? (
          <span>{label || term.value || 'Choose'}</span>
        ) : term.kind === 'constant' ? (
          <input
            className="w-24 bg-transparent outline-none"
            placeholder="Number"
            value={term.value}
            onChange={(event) => onChange({ ...term, value: event.target.value })}
          />
        ) : (
          <button type="button" className="text-left" onClick={() => setOpen((current) => !current)}>
            {label || 'Choose'}
          </button>
        )}
        {!locked && term.kind === 'constant' ? (
          <button type="button" className="ml-1 text-gray-500" aria-label="Change value" onClick={() => setOpen((current) => !current)}>▾</button>
        ) : null}
        {canRemove ? (
          <button type="button" className="ml-1 text-gray-500" aria-label="Remove from formula" onClick={onRemove}>×</button>
        ) : null}
      </span>
      {open ? (
        <OperandMenu
          providers={providers}
          parameters={parameters}
          namedValues={namedValues}
          onChoose={choose}
        />
      ) : null}
    </span>
  );
}

function OperandMenu({ providers, parameters, namedValues, onChoose }) {
  const namedParameters = parameters.filter((row) => row.key && row.label);
  const projectData = providers.filter((provider) => provider.evaluation_behavior !== 'run_entered');
  const runInputs = providers.filter((provider) => provider.evaluation_behavior === 'run_entered');
  return (
    <div className="absolute left-0 top-full z-30 mt-1 w-72 max-h-80 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg py-1">
      {projectData.length ? (
        <MenuGroup title="Project Data">
          {projectData.map((provider) => (
            <ProviderChoice key={provider.key} provider={provider} onChoose={onChoose} />
          ))}
        </MenuGroup>
      ) : null}
      {runInputs.length ? (
        <MenuGroup title="Run Input">
          {runInputs.map((provider) => (
            <ProviderChoice key={provider.key} provider={provider} onChoose={onChoose} />
          ))}
        </MenuGroup>
      ) : null}
      {namedParameters.length ? (
        <MenuGroup title="Parameters">
          {namedParameters.map((row) => (
            <button
              key={row.id}
              type="button"
              className="block w-full text-left px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-50"
              onClick={() => onChoose({ kind: 'parameter', key: row.key, value: '' })}
            >
              {row.label}
            </button>
          ))}
        </MenuGroup>
      ) : null}
      {namedValues.length ? (
        <MenuGroup title="Calculated Values">
          {namedValues.map((row) => (
            <button
              key={row.id}
              type="button"
              className="block w-full text-left px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-50"
              onClick={() => onChoose({ kind: 'named', key: row.key, value: '' })}
            >
              {row.label || row.key}
            </button>
          ))}
        </MenuGroup>
      ) : null}
      <MenuGroup title="Other">
        <button
          type="button"
          className="block w-full text-left px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-50"
          onClick={() => onChoose({ kind: 'constant', key: '', value: '' })}
        >
          Number
        </button>
      </MenuGroup>
    </div>
  );
}

function ProviderChoice({ provider, onChoose }) {
  return (
    <button
      type="button"
      className="block w-full text-left px-3 py-1.5 hover:bg-gray-50"
      onClick={() => onChoose({ kind: 'input', key: provider.key, value: '' })}
    >
      <span className="block text-sm text-gray-900">{provider.name}</span>
      {provider.evaluation_behavior_label ? (
        <span className="block text-xs text-gray-500">{provider.evaluation_behavior_label}</span>
      ) : null}
    </button>
  );
}

function MenuGroup({ title, children }) {
  return (
    <div className="py-1">
      <div className="px-3 pt-1 pb-1 text-xs font-semibold text-gray-500">{title}</div>
      {children}
    </div>
  );
}

function prettyExpression(expression, parameters) {
  let text = expression || '';
  (parameters || []).forEach((row) => {
    if (row.unit !== 'percent' || !row.display) return;
    const friendly = parameterPrimary(row);
    if (!friendly || friendly === row.display) return;
    const escaped = row.display.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    text = text.replace(new RegExp(`(^|\\s)${escaped}(?=\\s|$)`, 'g'), `$1${friendly}`);
  });
  return text;
}

function FormulaReadout({ expression }) {
  if (!expression) return null;
  const parts = expression.split(/(\s[+\u2212×÷]\s)/);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {parts.map((part, index) => (
        /^\s[+\u2212×÷]\s$/.test(part) ? (
          <span key={`${part}-${index}`} className="text-base text-gray-900">{part.trim()}</span>
        ) : (
          <span key={`${part}-${index}`} className="inline-flex px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-900">{part}</span>
        )
      ))}
    </div>
  );
}

function PreviewResult({ preview, onEditSetup }) {
  const [openScopes, setOpenScopes] = useState({});
  const title = preview.draft_preview
    ? 'Test Result'
    : (preview.name || 'Calculation Review');
  const projectCosts = (preview.accounting?.lines || []).filter((line) => line.creates_project_cost);
  const basis = [
    ...(preview.shared_inputs || []),
    ...((preview.groups || []).flatMap((group) => group.inputs || [])),
  ];
  return (
    <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">{title}</h2>
      <p className="text-sm text-gray-700 mb-4">
        {preview.version_label ? `${preview.version_label}. ` : ''}
        {preview.period_label || ''}
      </p>
      {preview.draft_preview ? (
        <p className="text-sm text-gray-600 mb-4">Test only. Nothing was saved or posted.</p>
      ) : null}
      {(preview.accounting?.errors || []).length ? (
        <div className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {(preview.accounting.errors || []).map((text) => <div key={text}>{explainCalculationError(text)}</div>)}
          {onEditSetup ? (
            <button type="button" className="btn-claret mt-3" onClick={onEditSetup}>Edit Project Setup</button>
          ) : null}
        </div>
      ) : null}
      {basis.length ? (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Basis</h3>
          {basis.map((input) => (
            <InputUsed key={`${input.provider_key}-${input.name}`} input={input} />
          ))}
        </div>
      ) : null}
      {preview.parameters.length ? <h3 className="text-sm font-semibold text-gray-900 mb-2">Rates</h3> : null}
      <ul className="text-sm text-gray-800 mb-4">
        {preview.parameters.map((row) => (
          <li key={row.key}>{row.label}: {parameterPrimary(row)}</li>
        ))}
      </ul>
      {(preview.groups || []).map((group) => (
        <div key={group.key} className="border-t border-gray-200 pt-4 mt-4">
          {(preview.groups || []).length > 1 ? <h3 className="font-semibold text-gray-900">{group.label}</h3> : null}
          {group.scope?.length ? (
            <div className="text-sm text-gray-700 mt-1">
              <button
                type="button"
                className="font-medium text-gray-900 text-left"
                aria-expanded={!!openScopes[group.key]}
                onClick={() => setOpenScopes((current) => ({ ...current, [group.key]: !current[group.key] }))}
              >
                Calculate from
                <span className="ml-2 font-normal text-gray-600">
                  {' '}{group.scope.length} cost code{group.scope.length === 1 ? '' : 's'} · {openScopes[group.key] ? 'Hide' : 'Show'}
                </span>
              </button>
              {openScopes[group.key] ? group.scope.map((pair) => (
                <div key={`${pair.cost_code_vuid}-${pair.cost_type_vuid}`} className="flex justify-between gap-4 max-w-xl mt-1">
                  <span>{pair.cost_code_label} / {pair.cost_type_label}</span>
                  {pair.display ? <span>{pair.display}</span> : null}
                </div>
              )) : null}
            </div>
          ) : null}
          <h3 className="text-sm font-semibold text-gray-900 mt-3 mb-2">Calculation</h3>
          {group.named_values.map((item) => (
            <div key={item.key} className="mb-4">
              <FormulaReadout expression={prettyExpression(item.expression, preview.parameters)} />
              <div className="mt-2 text-base font-semibold text-gray-900">{item.label}: {item.display}</div>
            </div>
          ))}
        </div>
      ))}
      {preview.recognition?.calculation_type === 'cumulative' ? (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">This period</h3>
          <p className="text-sm text-gray-800 mb-2">{preview.recognition.message}</p>
          {(preview.recognition.lines || []).map((row) => (
            <div key={`${row.group_key}-${row.amount_key}`} className="text-sm text-gray-800 mb-1">
              {row.label}: calculated total {row.cumulative_display}. Already recorded {row.previously_recorded_display}. This period {row.period_display}.
            </div>
          ))}
        </div>
      ) : null}
      {projectCosts.length ? (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Project Cost</h3>
          {projectCosts.map((line) => (
            <div key={`${line.group_key}-${line.line_number}`} className="text-sm text-gray-800 mb-2">
              <div>Destination: {line.destination_cost_code_label || 'Not set'}{line.destination_cost_type_label ? ` / ${line.destination_cost_type_label}` : ''}</div>
              <div>Amount: {line.amount_display}</div>
            </div>
          ))}
        </div>
      ) : null}
      {preview.accounting ? (
        <AccountingReview accounting={preview.accounting} run={preview.run} reversal={preview.reversal} />
      ) : null}
    </section>
  );
}

function AccountingReview({ accounting, run, reversal }) {
  const posted = run?.status === 'posted';
  const reversed = run?.status === 'reversed';
  const status = run?.status
    ? runStatusLabel(run.status, accounting.nothing_to_post)
    : (accounting.nothing_to_post ? 'Nothing to post' : ((accounting.errors || []).length ? 'Invalid' : 'Ready to Post'));
  const ready = status === 'Ready to Post' || status === 'Nothing to post' || status === 'Posted' || status === 'Reversed';
  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{reversed ? 'Original Posting' : 'Journal'}</h3>
      {posted || reversed ? (
        <div className="text-sm text-gray-800 mb-3">
          <div>Posted by {run.posted_by || 'unknown'}</div>
          <div>Posted at {run.posted_at ? new Date(run.posted_at).toLocaleString() : '—'}</div>
          {run.reference_number ? <div>Original reference {run.reference_number}</div> : null}
        </div>
      ) : null}
      {reversed && reversal ? (
        <div className="text-sm text-gray-800 mb-3">
          <div>Reversed by {reversal.reversed_by || 'unknown'}</div>
          <div>Reversed at {reversal.reversed_at ? new Date(reversal.reversed_at).toLocaleString() : '—'}</div>
          {reversal.reversal_reference_number ? <div>Reversal reference {reversal.reversal_reference_number}</div> : null}
          {reversal.reason ? <div>Reason: {reversal.reason}</div> : null}
          {reversal.integrity_warning ? <div className="text-amber-800">{reversal.integrity_warning}</div> : null}
        </div>
      ) : null}
      {accounting.nothing_to_post ? (
        <p className="text-sm text-gray-700 mb-2">Nothing to post.</p>
      ) : null}
      <table className="min-w-full text-sm mb-3">
        <thead className="text-left text-gray-600">
          <tr>
            <th className="py-2 pr-3">Account</th>
            <th className="py-2 pr-3 text-right">Debit</th>
            <th className="py-2 pr-3 text-right">Credit</th>
            <th className="py-2">Project Cost</th>
          </tr>
        </thead>
        <tbody>
          {(accounting.lines || []).map((line) => (
            <tr key={`${line.group_key}-${line.line_number}`} className="border-t border-gray-200">
              <td className="py-2 pr-3">
                <div>{line.account_label || line.account_role_label || 'Account not resolved'}</div>
                <div className="text-xs text-gray-500">{line.configured_side === 'credit' ? 'Credit' : 'Debit'} {line.amount_label}</div>
                {line.configured_side !== line.resolved_side ? (
                  <div className="text-xs text-amber-800">Negative amount is on the opposite side.</div>
                ) : null}
              </td>
              <td className="py-2 pr-3 text-right">{line.resolved_side === 'debit' ? line.amount_display : ''}</td>
              <td className="py-2 pr-3 text-right">{line.resolved_side === 'credit' ? line.amount_display : ''}</td>
              <td className="py-2">
                {line.creates_project_cost
                  ? `${line.destination_cost_code_label || 'Destination'} / ${line.destination_cost_type_label || ''}`.replace(/ \/ $/, '')
                  : (line.job_cost_choice === 'journal_only' ? 'Journal only' : 'No')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-sm text-gray-800">
        <div>Total Debits: {accounting.total_debits_display}</div>
        <div>Total Credits: {accounting.total_credits_display}</div>
        <div>Difference: {accounting.difference_display}</div>
      </div>
      <p className={`mt-2 text-sm font-medium ${ready ? 'text-gray-900' : 'text-red-800'}`}>Status: {status}</p>
      {run?.assignment_version_changed ? (
        <p className="mt-2 text-sm text-amber-800">
          {posted || reversed
            ? 'The project assignment has moved to another version. This result stays on the version that was posted.'
            : 'The project assignment version changed after this run. This review stays on the version that created it.'}
        </p>
      ) : null}
      {reversed && reversal ? (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Reversal</h3>
          <p className="text-sm text-gray-600 mb-2">Equal-and-opposite accounting. The original calculation remains in history.</p>
          <table className="min-w-full text-sm mb-3">
            <thead className="text-left text-gray-600">
              <tr>
                <th className="py-2 pr-3">Account</th>
                <th className="py-2 pr-3 text-right">Debit</th>
                <th className="py-2 pr-3 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {(reversal.lines || []).map((line) => (
                <tr key={line.line_number} className="border-t border-gray-200">
                  <td className="py-2 pr-3">{line.account_label}</td>
                  <td className="py-2 pr-3 text-right">{line.debit_display}</td>
                  <td className="py-2 pr-3 text-right">{line.credit_display}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-sm text-gray-800">
            <div>Total Debits: {reversal.total_debits_display}</div>
            <div>Total Credits: {reversal.total_credits_display}</div>
          </div>
          {(reversal.expenses || []).length ? (
            <div className="mt-3 text-sm text-gray-800">
              <div className="font-medium mb-1">Project costs</div>
              {reversal.expenses.map((expense) => (
                <div key={expense.vuid}>{expense.reversing ? 'Reversing' : 'Original'} {expense.expense_number}: {expense.amount_display}</div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function InputUsed({ input }) {
  return (
    <div className="text-sm text-gray-800 mb-3">
      <div className="font-medium">{input.name}: {input.display}</div>
      {(input.detail?.components || []).map((row) => (
        <div key={row.key} className="flex justify-between max-w-md text-gray-700">
          <span>{row.label}</span>
          <span>{row.display}</span>
        </div>
      ))}
      {input.notice ? <div className="text-amber-800">{input.notice}</div> : null}
      {(input.exclusions || []).map((text) => (
        <div key={text} className="text-gray-500">{text}</div>
      ))}
      {input.detail?.entered_by ? <div className="text-gray-500">Entered by {input.detail.entered_by}</div> : null}
    </div>
  );
}

export {
  applyLabel,
  emptyGroup,
  emptyNamed,
  emptyPair,
  emptyTerm,
  formulaTree,
  retarget,
  uniqueKey,
  CalculatedValueCard,
  PreviewResult,
};
